import { hotTopics } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

const badgeStyles: Record<string, string> = {
  "热": "bg-primary-500 text-background-50",
  "争": "bg-secondary-500 text-background-50",
  "聊": "bg-accent-500 text-background-50",
  "梗": "bg-secondary-100 text-secondary-800",
  "吵": "bg-primary-100 text-primary-700",
  "议": "bg-accent-100 text-accent-700",
};

const trendMap: Record<string, { icon: string; cls: string; label: string }> = {
  up: { icon: "ri-arrow-up-line", cls: "text-accent-600", label: "热度上升" },
  down: { icon: "ri-arrow-down-line", cls: "text-primary-600", label: "热度下降" },
  flat: { icon: "ri-arrow-right-line", cls: "text-foreground-400", label: "热度平稳" },
};

const rankCls = (i: number) =>
  i === 0
    ? "bg-primary-500 text-background-50"
    : i === 1
    ? "bg-secondary-500 text-background-50"
    : i === 2
    ? "bg-accent-500 text-background-50"
    : "bg-background-200 text-foreground-600";

const hotWords = [
  "口碑反转",
  "人设崩塌",
  "二创侵权",
  "情绪价值",
  "先涨后降",
  "全民吃瓜",
  "纯爱战神",
  "行业整顿",
  "饭圈化",
  "返乡观察",
];

export default function HotTopics() {
  return (
    <section id="hot" className="bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeading
            kicker="热搜风向 · 实时更新"
            title="今日热议榜单"
            desc="按讨论热度实时排序，点进话题可围观不同派别的热评与最新动态。"
          />
          <a
            href="#news"
            className="inline-flex items-center whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            查看全部动态
            <i className="ri-arrow-right-line ml-1.5" aria-hidden="true"></i>
          </a>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ol className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
              {hotTopics.map((t, i) => {
                const tr = trendMap[t.trend];
                return (
                  <li key={t.title}>
                    <a
                      href="#reviews"
                      className="group flex items-center gap-4 px-4 py-4 transition-colors hover:bg-background-100 md:px-6"
                    >
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${rankCls(i)}`}
                        aria-hidden="true"
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                          {t.title}
                        </span>
                        <span className="mt-1 flex items-center gap-2 text-xs text-foreground-600">
                          <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-800">{t.tag}</span>
                          <span>热度 {t.heat}</span>
                          <span className="hidden sm:inline">· {t.comments} 讨论</span>
                        </span>
                      </span>
                      <span
                        className={`hidden items-center gap-1 text-xs sm:inline-flex ${tr.cls}`}
                        title={tr.label}
                      >
                        <i className={tr.icon} aria-hidden="true"></i>
                        {tr.label}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeStyles[t.badge]}`}>
                        {t.badge}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </div>

          <aside className="space-y-6" aria-label="本周热词与上榜说明">
            <div className="rounded-2xl border border-background-200 bg-background-50 p-6">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-price-tag-3-line text-primary-500" aria-hidden="true"></i>
                本周热词
              </h3>
              <p className="mt-1 text-xs text-foreground-600">高频出现在各路热评里的词，围观时记得带上语境。</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {hotWords.map((w, i) => (
                  <span
                    key={w}
                    className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${
                      i < 3
                        ? "bg-secondary-100 text-secondary-800"
                        : "bg-background-100 text-foreground-700"
                    }`}
                  >
                    #{w}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-accent-200 bg-accent-50/70 p-6">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
                <i className="ri-information-line text-accent-700" aria-hidden="true"></i>
                怎么读这份榜单
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                榜单反映的是<b>讨论热度</b>，不代表观点对错。热度越高说明参与的声音越多，也更需要你带着判断去吃瓜，
                别急着跟风表态。
              </p>
              <a
                href="#debate"
                className="mt-4 inline-flex items-center whitespace-nowrap text-sm font-medium text-accent-700 hover:text-accent-800"
              >
                去看看争议观察
                <i className="ri-arrow-right-line ml-1.5" aria-hidden="true"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}